import type { Locale } from '@ai-radar/shared';

/**
 * Interface strings.
 *
 * Hand-written in both languages, never machine translated: a wrong nav
 * label or section header is on every page, which is more damaging than a
 * slightly stiff story summary. Story content is translated by the
 * pipeline; the chrome is not.
 */
export interface Dictionary {
  nav: {
    today: string;
    news: string;
    tools: string;
    models: string;
    updates: string;
    pricing: string;
    search: string;
  };
  home: {
    live: string;
    headlineZero: string;
    headlineOne: string;
    /** {count} is substituted. */
    headlineMany: string;
    importantToday: string;
    newTools: string;
    productUpdates: string;
    newModels: string;
    pricingChanges: string;
    trending: string;
    worthTrying: string;
    viewAll: string;
    emptyTitle: string;
    emptyBody: string;
  };
  story: {
    whatChanged: string;
    whyItMatters: string;
    whoShouldCare: string;
    worthTrying: string;
    source: string;
    alsoCovered: string;
    related: string;
    verified: string;
    back: string;
  };
  worth: {
    yes: string;
    no: string;
    maybe: string;
  };
  audience: Record<string, string>;
  time: {
    now: string;
    minute: string;
    hour: string;
    day: string;
    week: string;
  };
  footer: {
    about: string;
    methodology: string;
    rss: string;
    contact: string;
    promise: string;
  };
  subscribe: {
    /** Inline signup, shown once on the homepage. */
    heading: string;
    blurb: string;
    placeholder: string;
    submit: string;
    submitting: string;
    /** Shown after signup — the reader must now check their inbox. */
    checkInbox: string;
    checkInboxBody: string;
    invalidEmail: string;
    failed: string;
    instantOption: string;
    privacy: string;

    confirmedTitle: string;
    confirmedBody: string;
    confirmFailedTitle: string;
    confirmFailedBody: string;

    unsubscribedTitle: string;
    unsubscribedBody: string;
    unsubscribeFailedTitle: string;
    unsubscribeFailedBody: string;

    preferencesTitle: string;
    wantsDigest: string;
    wantsDigestHelp: string;
    wantsInstant: string;
    wantsInstantHelp: string;
    threshold: string;
    thresholdHelp: string;
    save: string;
    saved: string;
    unsubscribeLink: string;
    backHome: string;
    telegramOption: string;
    telegramNote: string;

    /** Telegram-first signup, while email signup is hidden. */
    telegramHeading: string;
    telegramBlurb: string;
    telegramCta: string;
    telegramFilterIntro: string;
    telegramFilterTopic: string;
    telegramFilterCompany: string;
    telegramFilterModel: string;
    telegramFilterHow: string;
  };
  meta: {
    advertisement: string;
    sponsored: string;
    sources: string;
    switchLanguage: string;
    theme: string;
  };
  auth: {
    signIn: string;
    signOut: string;
    register: string;
    account: string;

    signInTitle: string;
    signInBlurb: string;
    registerTitle: string;
    registerBlurb: string;

    email: string;
    password: string;
    newPassword: string;
    currentPassword: string;
    forgot: string;
    noAccount: string;
    haveAccount: string;
    continueWith: string;
    or: string;

    checkInbox: string;
    checkInboxBody: string;
    signedOut: string;

    /** Account settings. */
    yourAlerts: string;
    yourAlertsHelp: string;
    filterTopics: string;
    filterCompanies: string;
    filterModels: string;
    everything: string;
    channels: string;
    channelsHelp: string;
    linkedLogins: string;
    activeSessions: string;
    thisDevice: string;
    signOutOthers: string;
    changePassword: string;
    save: string;
    saved: string;
    deleteAccount: string;
    deleteAccountHelp: string;
    deleteAccountConfirm: string;
    deleteAccountFailed: string;

    genericError: string;
    /** Shown when someone lands on a signed-in page signed out. */
    signInRequired: string;

    /** Verify and reset, reached from an email. */
    verifiedTitle: string;
    verifiedBody: string;
    verifyFailedTitle: string;
    verifyFailedBody: string;
    resetTitle: string;
    resetBody: string;
    resetButton: string;
    resetDoneTitle: string;
    resetDoneBody: string;
    resetFailedTitle: string;
    resetFailedBody: string;
    accountBackHome: string;
    forgotTitle: string;
    forgotBody: string;
    forgotButton: string;
    forgotSentTitle: string;
    forgotSentBody: string;
  };
}

const en: Dictionary = {
  nav: {
    today: 'Today',
    news: 'News',
    tools: 'Tools',
    models: 'Models',
    updates: 'Updates',
    pricing: 'API & Pricing',
    search: 'Search',
  },
  home: {
    live: 'Live',
    headlineZero: 'A quiet day in AI. Nothing significant to report yet.',
    headlineOne: 'One thing worth knowing in AI today.',
    headlineMany: '{count} important things happened in AI today.',
    importantToday: 'Important today',
    newTools: 'New AI tools',
    productUpdates: 'Product updates',
    newModels: 'New models',
    pricingChanges: 'Pricing & API changes',
    trending: 'Trending',
    worthTrying: 'Worth trying',
    viewAll: 'View all',
    emptyTitle: 'The editor has not published anything yet.',
    emptyBody:
      'Ingestion is running and candidates are queued. Stories appear here once the editorial pipeline has judged and verified them.',
  },
  story: {
    whatChanged: 'What changed',
    whyItMatters: 'Why it matters',
    whoShouldCare: 'Who should care',
    worthTrying: 'Worth trying',
    source: 'Source',
    alsoCovered: 'Also covered by',
    related: 'Related',
    verified: 'Verified against primary source',
    back: 'Back to today',
  },
  worth: { yes: 'Yes', no: 'No', maybe: 'Maybe' },
  audience: {
    developers: 'Developers',
    founders: 'Founders',
    marketers: 'Marketers',
    creators: 'Creators',
    researchers: 'Researchers',
    enterprises: 'Enterprises',
  },
  time: { now: 'now', minute: 'm', hour: 'h', day: 'd', week: 'w' },
  footer: {
    about: 'About',
    methodology: 'Methodology',
    rss: 'RSS',
    contact: 'Contact',
    promise: 'Everything important in AI. Nothing else.',
  },
  subscribe: {
    heading: 'Get it by email',
    // States the frequency and the restraint, because both are the offer.
    blurb: 'One email each morning with what actually mattered. Nothing on quiet days.',
    placeholder: 'you@company.com',
    submit: 'Subscribe',
    submitting: 'Sending…',
    checkInbox: 'Check your inbox',
    checkInboxBody:
      'We sent a confirmation link. Click it and the digest starts tomorrow morning.',
    invalidEmail: 'That does not look like an email address.',
    failed: 'Something went wrong. Try again in a moment.',
    instantOption: 'Also alert me immediately when something major happens',
    privacy: 'One click to unsubscribe. We store your address and nothing else.',

    confirmedTitle: 'You are subscribed',
    confirmedBody:
      'The digest arrives each morning, on days when something worth reading happened.',
    confirmFailedTitle: 'That link did not work',
    confirmFailedBody:
      'It may have already been used, or it may have expired. Subscribing again will send a fresh one.',

    unsubscribedTitle: 'Unsubscribed',
    unsubscribedBody: 'You will not receive anything further. No hard feelings.',
    unsubscribeFailedTitle: 'That link did not work',
    unsubscribeFailedBody: 'You may already be unsubscribed.',

    preferencesTitle: 'Your alerts',
    wantsDigest: 'Daily digest',
    wantsDigestHelp: 'One email each morning with the day’s important stories.',
    wantsInstant: 'Instant alerts',
    wantsInstantHelp: 'An email the moment something major is published.',
    threshold: 'Alert me above',
    thresholdHelp: 'Higher means fewer, more significant alerts.',
    save: 'Save',
    saved: 'Saved',
    unsubscribeLink: 'Unsubscribe from everything',
    backHome: 'Back to AI Radar',
    telegramOption: 'Or get it on Telegram',
    // The whole reason to offer Telegram: it removes the signup objection.
    telegramNote: 'No email needed.',

    telegramHeading: 'Get it on Telegram',
    telegramBlurb:
      'One message each morning with what actually mattered. Nothing on quiet days. No email, no password.',
    telegramCta: 'Open the bot',
    // Filtering is the reason to sign up, not a settings detail — so it
    // is stated before the reader commits, not after.
    telegramFilterIntro: 'Or narrow it to only what you care about:',
    telegramFilterTopic: 'a topic — models, pricing, research',
    telegramFilterCompany: 'a company — OpenAI, Anthropic, Google',
    telegramFilterModel: 'a model — Claude, GPT, Gemini',
    telegramFilterHow: 'Send /category, /company or /model to the bot at any time.',
  },
  meta: {
    advertisement: 'Advertisement',
    sponsored: 'Sponsored',
    sources: 'sources',
    switchLanguage: 'العربية',
    theme: 'Theme',
  },
  auth: {
    signIn: 'Sign in',
    signOut: 'Sign out',
    register: 'Create account',
    account: 'Account',

    signInTitle: 'Sign in',
    // States what an account is for. It is not a gate, so the copy has
    // to say what the reader gets rather than imply they need one.
    signInBlurb: 'Keep your filters and saved stories across the web and the app.',
    registerTitle: 'Create an account',
    registerBlurb:
      'Set your filters once and they follow you everywhere. Reading AI Radar never requires one.',

    email: 'Email',
    password: 'Password',
    newPassword: 'New password',
    currentPassword: 'Current password',
    forgot: 'Forgot your password?',
    noAccount: 'No account yet?',
    haveAccount: 'Already have an account?',
    continueWith: 'Continue with',
    or: 'or',

    checkInbox: 'Check your inbox',
    checkInboxBody: 'If that address is not already registered, a confirmation link is on its way.',
    signedOut: 'Signed out.',

    yourAlerts: 'Your alerts',
    yourAlertsHelp: 'These apply everywhere — email, Telegram and the app.',
    filterTopics: 'Topics',
    filterCompanies: 'Companies',
    filterModels: 'Models',
    everything: 'Everything',
    channels: 'Where you get alerts',
    channelsHelp: 'Connect a channel and these filters apply to it automatically.',
    linkedLogins: 'Linked logins',
    activeSessions: 'Active sessions',
    thisDevice: 'This device',
    signOutOthers: 'Sign out everywhere else',
    changePassword: 'Change password',
    save: 'Save',
    saved: 'Saved',
    deleteAccount: 'Delete account',
    deleteAccountHelp:
      'Permanently deletes your account, saved stories, preferences, sessions and linked alert channels.',
    deleteAccountConfirm:
      'Delete your account and all associated server data? This cannot be undone.',
    deleteAccountFailed: 'Could not delete your account. Try again in a moment.',

    genericError: 'Something went wrong. Try again in a moment.',
    signInRequired: 'Sign in to see this.',

    verifiedTitle: 'Address confirmed',
    verifiedBody: 'Thank you. You can close this tab.',
    verifyFailedTitle: 'That link did not work',
    verifyFailedBody:
      'It may have expired or already been used. Sign in and request a new one.',
    resetTitle: 'Choose a new password',
    // Says what makes a good one rather than listing rules it must obey.
    resetBody: 'At least 10 characters. A phrase you can remember beats a short jumble.',
    resetButton: 'Set password',
    resetDoneTitle: 'Password changed',
    resetDoneBody: 'You have been signed out everywhere. Sign in with your new password.',
    resetFailedTitle: 'That link did not work',
    resetFailedBody:
      'Reset links expire after 30 minutes and work only once. Request a new one.',
    accountBackHome: 'Back to AI Radar',
    forgotTitle: 'Reset your password',
    forgotBody: 'Enter your address and we will send a link.',
    forgotButton: 'Send the link',
    forgotSentTitle: 'Check your inbox',
    // Non-committal on purpose: the API cannot say whether the address
    // is registered, and neither can this.
    forgotSentBody: 'If that address has an account, a reset link is on its way.',
  },
};

const ar: Dictionary = {
  nav: {
    today: 'اليوم',
    news: 'أخبار',
    tools: 'أدوات',
    models: 'نماذج',
    updates: 'تحديثات',
    pricing: 'الأسعار والواجهات',
    search: 'بحث',
  },
  home: {
    live: 'مباشر',
    headlineZero: 'يوم هادئ في الذكاء الاصطناعي. لا جديد يستحق الذكر بعد.',
    headlineOne: 'أمر واحد يستحق المعرفة اليوم.',
    headlineMany: '{count} أمور مهمة حدثت في الذكاء الاصطناعي اليوم.',
    importantToday: 'الأهم اليوم',
    newTools: 'أدوات جديدة',
    productUpdates: 'تحديثات المنتجات',
    newModels: 'نماذج جديدة',
    pricingChanges: 'الأسعار والواجهات',
    trending: 'الأكثر تداولاً',
    worthTrying: 'يستحق التجربة',
    viewAll: 'الكل',
    emptyTitle: 'لم ينشر المحرر أي شيء بعد.',
    emptyBody:
      'جمع المصادر يعمل والمواد في الانتظار. تظهر الأخبار هنا بعد أن يقيّمها المحرر ويتحقق منها.',
  },
  story: {
    whatChanged: 'ما الذي تغير',
    whyItMatters: 'لماذا يهم',
    whoShouldCare: 'لمن يهم',
    worthTrying: 'يستحق التجربة',
    source: 'المصدر',
    alsoCovered: 'غطته أيضاً',
    related: 'ذات صلة',
    verified: 'تم التحقق من المصدر الأصلي',
    back: 'عودة إلى اليوم',
  },
  worth: { yes: 'نعم', no: 'لا', maybe: 'ربما' },
  audience: {
    developers: 'المطورون',
    founders: 'المؤسسون',
    marketers: 'المسوقون',
    creators: 'صناع المحتوى',
    researchers: 'الباحثون',
    enterprises: 'الشركات',
  },
  // Arabic abbreviations for relative time. "الآن" for now, then
  // single-letter markers so the metadata line stays compact.
  time: { now: 'الآن', minute: 'د', hour: 'س', day: 'ي', week: 'أ' },
  footer: {
    about: 'عن الموقع',
    methodology: 'المنهجية',
    rss: 'RSS',
    contact: 'اتصل بنا',
    promise: 'كل ما يهم في الذكاء الاصطناعي. لا شيء غير ذلك.',
  },
  subscribe: {
    heading: 'استقبله بالبريد',
    blurb: 'رسالة واحدة كل صباح بما يستحق فعلاً. ولا شيء في الأيام الهادئة.',
    placeholder: 'you@company.com',
    submit: 'اشترك',
    submitting: 'جارٍ الإرسال…',
    checkInbox: 'تفقّد بريدك',
    checkInboxBody: 'أرسلنا رابط تأكيد. اضغط عليه ويبدأ الملخص صباح الغد.',
    invalidEmail: 'هذا لا يبدو عنوان بريد صحيح.',
    failed: 'حدث خطأ ما. حاول بعد قليل.',
    instantOption: 'نبّهني فوراً عند حدوث شيء كبير',
    privacy: 'إلغاء الاشتراك بضغطة واحدة. نحفظ عنوانك فقط ولا شيء غيره.',

    confirmedTitle: 'تم اشتراكك',
    confirmedBody: 'يصلك الملخص كل صباح، في الأيام التي يحدث فيها ما يستحق القراءة.',
    confirmFailedTitle: 'الرابط لم يعمل',
    confirmFailedBody: 'ربما استُخدم من قبل أو انتهت صلاحيته. الاشتراك مجدداً يرسل رابطاً جديداً.',

    unsubscribedTitle: 'تم إلغاء الاشتراك',
    unsubscribedBody: 'لن يصلك شيء بعد الآن.',
    unsubscribeFailedTitle: 'الرابط لم يعمل',
    unsubscribeFailedBody: 'ربما تكون قد ألغيت اشتراكك بالفعل.',

    preferencesTitle: 'تنبيهاتك',
    wantsDigest: 'الملخص اليومي',
    wantsDigestHelp: 'رسالة واحدة كل صباح بأهم أخبار اليوم.',
    wantsInstant: 'تنبيهات فورية',
    wantsInstantHelp: 'رسالة فور نشر خبر كبير.',
    threshold: 'نبّهني فوق',
    thresholdHelp: 'كلما ارتفع الرقم قلّت التنبيهات وزادت أهميتها.',
    save: 'حفظ',
    saved: 'تم الحفظ',
    unsubscribeLink: 'إلغاء الاشتراك من كل شيء',
    backHome: 'العودة إلى AI Radar',
    telegramOption: 'أو استقبله على تيليجرام',
    telegramNote: 'دون الحاجة إلى بريد.',

    telegramHeading: 'استقبله على تيليجرام',
    telegramBlurb:
      'رسالة واحدة كل صباح بما يهم فعلاً. ولا شيء في الأيام الهادئة. دون بريد ودون كلمة مرور.',
    telegramCta: 'افتح البوت',
    telegramFilterIntro: 'أو حدّده بما يهمك وحده:',
    telegramFilterTopic: 'موضوع — النماذج، الأسعار، الأبحاث',
    telegramFilterCompany: 'شركة — OpenAI أو Anthropic أو Google',
    telegramFilterModel: 'نموذج — Claude أو GPT أو Gemini',
    telegramFilterHow: 'أرسل /category أو /company أو /model إلى البوت في أي وقت.',
  },
  meta: {
    advertisement: 'إعلان',
    sponsored: 'مُموّل',
    sources: 'مصادر',
    switchLanguage: 'English',
    theme: 'المظهر',
  },
  auth: {
    signIn: 'تسجيل الدخول',
    signOut: 'تسجيل الخروج',
    register: 'إنشاء حساب',
    account: 'الحساب',

    signInTitle: 'تسجيل الدخول',
    signInBlurb: 'احتفظ بفلاترك وموادك المحفوظة عبر الموقع والتطبيق.',
    registerTitle: 'إنشاء حساب',
    registerBlurb:
      'اضبط فلاترك مرة واحدة وستتبعك أينما كنت. قراءة AI Radar لا تتطلب حساباً أبداً.',

    email: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    newPassword: 'كلمة المرور الجديدة',
    currentPassword: 'كلمة المرور الحالية',
    forgot: 'نسيت كلمة المرور؟',
    noAccount: 'ليس لديك حساب؟',
    haveAccount: 'لديك حساب بالفعل؟',
    continueWith: 'المتابعة عبر',
    or: 'أو',

    checkInbox: 'تحقق من بريدك',
    checkInboxBody: 'إن لم يكن هذا العنوان مسجلاً من قبل، فرابط التأكيد في طريقه إليك.',
    signedOut: 'تم تسجيل الخروج.',

    yourAlerts: 'تنبيهاتك',
    yourAlertsHelp: 'تنطبق في كل مكان — البريد وتيليجرام والتطبيق.',
    filterTopics: 'المواضيع',
    filterCompanies: 'الشركات',
    filterModels: 'النماذج',
    everything: 'كل شيء',
    channels: 'أين تصلك التنبيهات',
    channelsHelp: 'اربط قناة وستنطبق هذه الفلاتر عليها تلقائياً.',
    linkedLogins: 'طرق الدخول المرتبطة',
    activeSessions: 'الجلسات النشطة',
    thisDevice: 'هذا الجهاز',
    signOutOthers: 'تسجيل الخروج من كل الأجهزة الأخرى',
    changePassword: 'تغيير كلمة المرور',
    save: 'حفظ',
    saved: 'تم الحفظ',
    deleteAccount: 'حذف الحساب',
    deleteAccountHelp:
      'يحذف حسابك وموادك المحفوظة وتفضيلاتك وجلساتك وقنوات التنبيه المرتبطة نهائياً.',
    deleteAccountConfirm:
      'هل تريد حذف حسابك وكل بياناته المرتبطة من الخادم؟ لا يمكن التراجع عن هذا الإجراء.',
    deleteAccountFailed: 'تعذّر حذف حسابك. حاول مرة أخرى بعد قليل.',

    genericError: 'حدث خطأ ما. حاول مرة أخرى بعد قليل.',
    signInRequired: 'سجّل الدخول لعرض هذا.',

    verifiedTitle: 'تم تأكيد العنوان',
    verifiedBody: 'شكراً لك. يمكنك إغلاق هذه الصفحة.',
    verifyFailedTitle: 'لم يعمل هذا الرابط',
    verifyFailedBody: 'ربما انتهت صلاحيته أو استُخدم من قبل. سجّل الدخول واطلب رابطاً جديداً.',
    resetTitle: 'اختر كلمة مرور جديدة',
    resetBody: 'عشرة أحرف على الأقل. عبارة تتذكرها أفضل من خليط قصير.',
    resetButton: 'تعيين كلمة المرور',
    resetDoneTitle: 'تم تغيير كلمة المرور',
    resetDoneBody: 'تم تسجيل خروجك من كل الأجهزة. سجّل الدخول بكلمة المرور الجديدة.',
    resetFailedTitle: 'لم يعمل هذا الرابط',
    resetFailedBody: 'تنتهي روابط الاستعادة خلال ٣٠ دقيقة وتعمل مرة واحدة فقط. اطلب رابطاً جديداً.',
    accountBackHome: 'العودة إلى AI Radar',
    forgotTitle: 'استعادة كلمة المرور',
    forgotBody: 'أدخل عنوانك وسنرسل لك رابطاً.',
    forgotButton: 'أرسل الرابط',
    forgotSentTitle: 'تحقق من بريدك',
    forgotSentBody: 'إن كان لهذا العنوان حساب، فرابط الاستعادة في طريقه إليك.',
  },
};

const DICTIONARIES: Record<Locale, Dictionary> = { en, ar };

export function getDictionary(locale: Locale): Dictionary {
  return DICTIONARIES[locale];
}

/** Substitute {count} in a headline template. */
export function interpolate(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, key) =>
    key in values ? String(values[key]) : match,
  );
}
